import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type { Repair, RepairStatus } from '@shared/definitions/repair';
import type { RepairLog } from '@shared/definitions/repair-log';



/**
 * Pattern B contract for Repairs.
 *
 * Workspace-scoped. Logs live in a subcollection and are loaded on demand.
 */
export interface IRepairsStore {
  readonly repairs: Signal<readonly Repair[]>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  readonly totalCount: Signal<number>;
  readonly openCount: Signal<number>;
  readonly completedCount: Signal<number>;
  readonly counts: Signal<{
    pending: number;
    inProgress: number;
    completed: number;
    canceled: number;
    waitingForParts: number;
    assigned: number;
    resolved: number;
    total: number;
  }>;

  /**
   * (Re)load the full workspace list. Idempotent by default: a no-op when the
   * live listener is already open on the same (user, workspace) scope. Pass
   * `force = true` for an explicit manual refresh (e.g. the interventions hub).
   */
  refresh(force?: boolean): Promise<void>;

  getRepairById(repairUID: string): Repair | null;
  getRepairsByProperty(propertyUID: string): readonly Repair[];
  getRepairsByStatus(status: RepairStatus): readonly Repair[];

  addRepair(repair: Repair): Promise<string>;
  updateRepair(repair: Repair): Promise<void>;
  deleteRepair(repairUID: string): Promise<void>;

  getRepairLogs(repairUID: string): Promise<readonly RepairLog[]>;
  addGeneralLog(repairUID: string, action: string, notes?: string, documentUIDs?: string[]): Promise<string>;
}

export const REPAIRS_STORE = new InjectionToken<IRepairsStore>('REPAIRS_STORE');

export type RepairsStoreMode = 'mock' | 'firestore';

export function resolveRepairsStoreMode(): RepairsStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('repairs.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('repairs.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}
